import type { Sensor, SensorHistory } from '../../interfaces';
import type { ChartSeries, ChartDataPoint } from '../interfaces/chart-data.interface';
import { mapWindDirection, normalizeWindDirectionCode } from '../../utils/wind-direction';

/**
 * SensorHistory の日時を Date オブジェクトに変換する。
 * MQTT が書き込む "YYYY-MM-DD HH:mm:ss" 形式の date フィールド(JST)と、
 * Firestore Timestamp 形式の measuredAt フィールドの両方に対応する。
 */
function getTimestamp(h: SensorHistory): Date | null {
  // 優先: MQTT 形式の date (文字列)
  if (h.date) {
    // "YYYY-MM-DD HH:mm:ss" を JST として解釈
    // ISO8601 形式に変換: "YYYY-MM-DDTHH:mm:ss+09:00"
    const isoStr = h.date.replace(' ', 'T') + '+09:00';
    const d = new Date(isoStr);
    if (!isNaN(d.getTime())) return d;
  }
  // フォールバック: Firestore Timestamp 形式
  if (h.measuredAt && typeof h.measuredAt.toDate === 'function') {
    return h.measuredAt.toDate();
  }
  return null;
}

/**
 * SensorHistory からフラットフィールドまたは旧 data ネスト内のフィールドを取得する。
 * MQTT はフラット構造で書き込むが、旧形式の data ネストにも後方互換で対応。
 */
function getFieldValue(h: SensorHistory, fieldName: string): unknown {
  // フラットフィールドを優先
  const flat = (h as unknown as Record<string, unknown>)[fieldName];
  if (flat !== undefined) return flat;
  // フォールバック: 旧形式の data ネスト
  if (h.data && typeof h.data === 'object') {
    return h.data[fieldName];
  }
  return undefined;
}

function extractSeries(
  sensors: Sensor[],
  histories: Map<string, SensorHistory[]>,
  labelPrefix: string,
  valueExtractor: (h: SensorHistory) => unknown,
  colorResolver?: (index: number) => string,
  extraExtractor?: (h: SensorHistory) => Record<string, unknown> | undefined
): ChartSeries[] {
  const seriesList: ChartSeries[] = [];
  
  sensors.forEach((sensor, index) => {
    const sensorHistories = histories.get(sensor.id) || [];
    const dataPoints: ChartDataPoint[] = [];

    sensorHistories.forEach(h => {
      const value = valueExtractor(h);
      const timestamp = getTimestamp(h);
      if (typeof value === 'number' && Number.isFinite(value) && timestamp) {
        const point: ChartDataPoint = {
          timestamp,
          value: value,
        };
        const extra = extraExtractor ? extraExtractor(h) : undefined;
        if (extra) {
          point.extra = extra;
        }
        dataPoints.push(point);
      }
    });

    if (dataPoints.length > 0) {
      seriesList.push({
        label: sensors.length > 1 ? `${labelPrefix}${index + 1}` : labelPrefix,
        data: dataPoints,
        color: colorResolver ? colorResolver(index) : undefined,
      });
    }
  });

  return seriesList;
}

export function mapWbgtSeries(sensors: Sensor[], histories: Map<string, SensorHistory[]>): ChartSeries[] {
  return extractSeries(sensors, histories, 'WBGT', h => getFieldValue(h, 'wbgt'));
}

export function mapTemperatureSeries(sensors: Sensor[], histories: Map<string, SensorHistory[]>): ChartSeries[] {
  return extractSeries(sensors, histories, '温度', h => getFieldValue(h, 'temperature'));
}

export function mapHumiditySeries(sensors: Sensor[], histories: Map<string, SensorHistory[]>): ChartSeries[] {
  return extractSeries(sensors, histories, '湿度', h => getFieldValue(h, 'humidity'));
}

export function mapWindSeries(sensors: Sensor[], histories: Map<string, SensorHistory[]>): ChartSeries[] {
  return extractSeries(
    sensors, 
    histories, 
    '風速', 
    h => {
      // MQTT は windSpeed フィールドに書き込む (isWindSpeed === true のレコードのみ)
      const speed = getFieldValue(h, 'windSpeed') ?? getFieldValue(h, 'speed');
      const isWind = getFieldValue(h, 'isWindSpeed');
      // isWindSpeed が明示的に false の場合（風向データ）はスキップ
      if (isWind === false) return undefined;
      return speed;
    },
    undefined,
    h => {
      const dir = getFieldValue(h, 'windDirection') ?? getFieldValue(h, 'direction');
      const name = mapWindDirection(dir);
      const code = normalizeWindDirectionCode(dir);
      return name && code !== undefined ? { windDirection: name, windDirectionCode: code } : undefined;
    }
  );
}

function extractMultiSeries(
  sensors: Sensor[],
  histories: Map<string, SensorHistory[]>,
  sensorPrefix: string,
  metrics: { key: string; labelSuffix: string }[]
): ChartSeries[] {
  const seriesList: ChartSeries[] = [];
  
  sensors.forEach((sensor, index) => {
    const sensorHistories = histories.get(sensor.id) || [];
    const dataMaps = new Map<string, ChartDataPoint[]>();
    metrics.forEach(m => dataMaps.set(m.key, []));

    sensorHistories.forEach(h => {
      const timestamp = getTimestamp(h);
      if (timestamp) {
        metrics.forEach(m => {
          const value = getFieldValue(h, m.key);
          if (typeof value === 'number' && Number.isFinite(value)) {
            dataMaps.get(m.key)!.push({ timestamp, value });
          }
        });
      }
    });

    const prefix = sensors.length > 1 ? `${sensorPrefix}${index + 1} ` : '';
    metrics.forEach(m => {
      const data = dataMaps.get(m.key)!;
      if (data.length > 0) {
        seriesList.push({ label: `${prefix}${m.labelSuffix}`, data });
      }
    });
  });

  return seriesList;
}

export function mapNoiseSeries(sensors: Sensor[], histories: Map<string, SensorHistory[]>): ChartSeries[] {
  return extractMultiSeries(sensors, histories, '騒音計', [
    { key: 'noiseL5', labelSuffix: 'L5' },
    { key: 'noiseMax', labelSuffix: 'Max' }
  ]);
}

export function mapVibrationSeries(sensors: Sensor[], histories: Map<string, SensorHistory[]>): ChartSeries[] {
  return extractMultiSeries(sensors, histories, '振動計', [
    { key: 'vibrationL10', labelSuffix: 'L10' },
    { key: 'vibrationMax', labelSuffix: 'Max' }
  ]);
}

export function mapPhSeries(sensors: Sensor[], histories: Map<string, SensorHistory[]>): ChartSeries[] {
  return extractSeries(sensors, histories, 'pH', h => getFieldValue(h, 'ph'));
}

export function mapTiltSeries(sensors: Sensor[], histories: Map<string, SensorHistory[]>): ChartSeries[] {
  return extractMultiSeries(sensors, histories, '傾斜計', [
    { key: 'tiltX', labelSuffix: 'X軸' },
    { key: 'tiltY', labelSuffix: 'Y軸' },
    { key: 'tiltZ', labelSuffix: 'Z軸' }
  ]);
}
